import {
  orderBookAxiosApi,
  calculatorAxiosApi,
  predictionManagerAxiosApi,
  openerOrchestratorAxiosApi,
  unwinderOrchestratorAxiosApi
} from 'src/Config/axiosApi';
import {
  Exchange,
  ExchangeStatus,
  CalculatorStats,
  ConnectorHealthResponse
} from 'src/Modules/Arbitrage/types';
import * as endpoints from './endpoints';
import qs from 'qs';

// Fetches available exchanges from orderbook-aggregator
export const getExchanges = async (): Promise<Exchange[]> => {
  const res = await orderBookAxiosApi.get<Exchange[]>(
    endpoints.EXCHANGES + '/available'
  );
  return res.data;
};

// Get exchanges status from orderbook-aggregator
export const getExchangesStatus = async (): Promise<ExchangeStatus[]> => {
  const res = await orderBookAxiosApi.get(endpoints.EXCHANGES + '/status');
  const statusMap = res.data;
  return Object.entries(statusMap).map(
    ([exchangeId, stats]: [string, any]) => ({
      id: exchangeId,
      name: exchangeId.charAt(0).toUpperCase() + exchangeId.slice(1),
      activePairs: stats.activePairs ?? 0,
      activeSymbols: stats.activeSymbols ?? 0,
      lastUpdate: stats.lastUpdate,
      totalUpdates: stats.totalUpdates ?? 0,
      available: stats.lastUpdate > Date.now() - 60000
    })
  );
};

// Get calculator stats from Rust arbitrage-calculator
export const getCalculatorStats = async (): Promise<CalculatorStats> => {
  const res = await calculatorAxiosApi.get('/arbitrage/cache/stats');
  return {
    totalPairs: res.data.total_pairs ?? 0,
    totalSymbols: res.data.total_symbols ?? 0,
    pairsWithMultipleExchanges: res.data.pairs_with_multiple_exchanges ?? 0,
    totalOrderbooks: res.data.total_orderbooks ?? 0,
    exchangeCoverage: res.data.exchange_coverage ?? {}
  };
};

// Get connector health data (what connectors are subscribing to vs publishing)
export const getConnectorHealth =
  async (): Promise<ConnectorHealthResponse> => {
    const res = await orderBookAxiosApi.get(
      endpoints.EXCHANGES + '/connectors/health'
    );
    return res.data;
  };

// Get updates status (is system running)
export const getUpdatesStatus = async (): Promise<{
  updatesEnabled: boolean;
}> => {
  const res = await orderBookAxiosApi.get(endpoints.EXCHANGES + '/updates');
  return res.data;
};

// Toggle updates on/off (legacy - only toggles local flag)
export const putToggleUpdates = async (restart?: boolean) => {
  const payload = restart ? { restart: true } : {};
  const res = await orderBookAxiosApi.put(
    endpoints.EXCHANGES + '/toggle-updates',
    payload
  );
  return res.data;
};

// =============================================================================
// SYSTEM CONTROL (On/Off with AWS scaling)
// =============================================================================

export type Subsystem = 'all' | 'orderbooks' | 'predictions';

export interface SystemState {
  isRunning: boolean;
  subsystems: {
    orderbooks: boolean;
    predictions: boolean;
  };
  clusters: {
    core: {
      name: string;
      region: string;
      nodeGroups: Array<{
        name: string;
        desiredSize: number;
        minSize: number;
        maxSize: number;
        status: string;
      }>;
    };
    connectors: {
      name: string;
      region: string;
      nodeGroups: Array<{
        name: string;
        desiredSize: number;
        minSize: number;
        maxSize: number;
        status: string;
      }>;
    };
  };
  lastUpdated: number;
}

export interface PredictionSystemState {
  isRunning: boolean;
  nodeGroup: {
    name: string;
    desiredSize: number;
    minSize: number;
    maxSize: number;
    status: string;
  } | null;
  lastUpdated: number;
}

/**
 * Per-domain presence reported by `/api/system/all/state` once the control
 * planes scale to 0 with their domain (P5).
 *
 * `booted` — the domain's orchestrator pod is up.
 * `nodeReady` — the domain's `position-*-node` pod is up. Rule CRUD is served
 *   by THAT pod, so a screen that writes rules gates on this, not on `booted`.
 * `nodeServing` — that same pod has a READY replica, i.e. the load balancer
 *   has a target and rule writes actually land. A pod scaled to 1 but still
 *   Pending reports `nodeReady: true` while every write dies as a bodyless
 *   503, so only this field can back an "opener is ON" claim.
 * `down` — an EXPLICIT `booted: false`. Never `!booted`: `booted`, `nodeReady`
 *   and `nodeServing` are all OMITTED when the backend could not read the
 *   Deployment, and treating that unknown as down claims a live domain is off.
 */
export interface DomainNodePresence {
  booted?: boolean;
  nodeReady?: boolean;
  nodeServing?: boolean;
  isRunning: boolean;
  down: boolean;
}

/**
 * `booted` — the orchestrator pod has >0 DESIRED replicas.
 * `serving` — it has a READY replica, i.e. requests actually land. Same split as
 *   `nodeReady`/`nodeServing` above, and a gate needs BOTH for the same reason:
 *   `booted` flips true the moment the scale patch lands, while the pod stays
 *   Pending and every read against it still dies at the load balancer.
 *
 * Both OPTIONAL, like `DomainNodePresence`'s: the backend omits them when it
 * could not read the Deployment. These two domains have a boot gate and a
 * Boot/Shutdown control of their own now, so an unknown arriving as `false`
 * would close the gate over a working pod.
 */
export interface DomainRunPresence {
  booted?: boolean;
  serving?: boolean;
  isRunning: boolean;
  down: boolean;
}

export interface DomainArmedPresence {
  booted?: boolean;
  serving?: boolean;
  armed: boolean;
  down: boolean;
}

export interface DomainWantedPresence {
  booted?: boolean;
  serving?: boolean;
  wanted: boolean;
  down: boolean;
}

export interface AllSystemDomains {
  opener: DomainNodePresence;
  unwinder: DomainNodePresence;
  opportunities: DomainRunPresence;
  matching: DomainArmedPresence;
  sniper: DomainWantedPresence;
}

// Aggregate prediction-side state for the master kill switch.
export interface AllSystemState {
  isRunning: boolean;
  subsystems: {
    predictions: boolean;
    unwinder: boolean;
    opener: boolean;
  };
  lastUpdated: number;
  /**
   * Additive and OPTIONAL — absent on backends predating P5. Consumers must
   * treat an absent field as "unknown" and behave exactly as before.
   */
  domains?: AllSystemDomains;
}

export interface SystemControlResult {
  success: boolean;
  message: string;
  nodeGroups?: string[];
  errors?: string[];
}

export interface SystemJobStep {
  label: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  error?: string;
}

export type SystemJobSubsystem = Subsystem | 'unwinder' | 'opener';

export interface SystemJob {
  id: string;
  type: 'start' | 'stop';
  subsystem: SystemJobSubsystem;
  status: 'running' | 'completed' | 'failed';
  steps: SystemJobStep[];
  currentStep: number;
  startedAt: number;
  completedAt?: number;
  error?: string;
  triggeredPanic?: boolean;
  /**
   * Enriched order-cancel counts returned by the STOP/PANIC control-plane job.
   * `*Cancelled` = orders the job actively cancelled at each platform;
   * `*Remaining` = orders still resting after the cancel sweep (> 0 means the
   * cancel was incomplete and must be surfaced prominently). `podForceDeleted`
   * is true when the engine pod had to be force-deleted before scaling to 0.
   * NOTE: Polymarket UI / proxy-wallet orders are NOT cancellable by panic.
   *
   * A `*Remaining` of **null** means UNKNOWN — the sweep could not be confirmed
   * (missing credentials, a listing that threw, an account it could not reach).
   * The backend states this contract explicitly and never reports either case as
   * 0, so NEITHER may be defaulted to 0 here: `null` is the outcome that most
   * needs the operator's eyes, and `?? 0` renders it as a clean book.
   * `undefined` is different again — the field is simply absent because this job
   * ran no cancel sweep at all.
   */
  kalshiCancelled?: number;
  polymarketCancelled?: number;
  kalshiRemaining?: number | null;
  polymarketRemaining?: number | null;
  podForceDeleted?: boolean;
}

export interface RestartConnectorsResult {
  success: boolean;
  message: string;
  restarted: string[];
  errors: string[];
  systemRunning: boolean;
}

export interface ConnectorExchangesResult {
  exchanges: string[];
  deployments: string[];
}

const SYSTEM_STATE_TIMEOUT_MS = 5000;
export type SystemStateScope = 'all' | 'predictions';

// Get combined system state from both orderbook-aggregator and prediction-manager
export const getSystemState = async (
  scope: SystemStateScope = 'all'
): Promise<SystemState> => {
  if (scope === 'predictions') {
    const predictionsRes = await predictionManagerAxiosApi.get(
      '/api/system/state',
      {
        timeout: SYSTEM_STATE_TIMEOUT_MS,
      }
    );
    const predictionsState = predictionsRes.data as PredictionSystemState;
    const predictionsRunning = predictionsState?.isRunning ?? false;

    return {
      isRunning: predictionsRunning,
      subsystems: {
        orderbooks: false,
        predictions: predictionsRunning,
      },
      clusters: {
        core: { name: '', region: '', nodeGroups: [] },
        connectors: { name: '', region: '', nodeGroups: [] },
      },
      lastUpdated: Date.now(),
    };
  }

  const [orderbooksRes, predictionsRes] = await Promise.allSettled([
    orderBookAxiosApi.get('/api/system/state', {
      timeout: SYSTEM_STATE_TIMEOUT_MS,
    }),
    predictionManagerAxiosApi.get('/api/system/state', {
      timeout: SYSTEM_STATE_TIMEOUT_MS,
    }),
  ]);

  const orderbooksState = orderbooksRes.status === 'fulfilled' ? orderbooksRes.value.data : null;
  const predictionsState: PredictionSystemState | null =
    predictionsRes.status === 'fulfilled' ? predictionsRes.value.data : null;

  const orderbooksRunning = orderbooksState?.subsystems?.orderbooks ?? orderbooksState?.isRunning ?? false;
  const predictionsRunning = predictionsState?.isRunning ?? false;

  return {
    isRunning: orderbooksRunning || predictionsRunning,
    subsystems: {
      orderbooks: orderbooksRunning,
      predictions: predictionsRunning,
    },
    clusters: orderbooksState?.clusters ?? {
      core: { name: '', region: '', nodeGroups: [] },
      connectors: { name: '', region: '', nodeGroups: [] },
    },
    lastUpdated: Date.now(),
  };
};

// Get system health
export const getSystemHealth = async (): Promise<{
  ready: boolean;
  k8sConnected: boolean;
}> => {
  const res = await orderBookAxiosApi.get('/api/system/health');
  return res.data;
};

// Start a subsystem
export const startSystem = async (
  subsystem?: Subsystem
): Promise<SystemJob> => {
  if (subsystem === 'predictions') {
    const res = await predictionManagerAxiosApi.post('/api/system/start');
    return res.data;
  }
  if (subsystem === 'all') {
    const [orderbooksRes, predictionsRes] = await Promise.allSettled([
      orderBookAxiosApi.post('/api/system/start', { subsystem: 'orderbooks' }),
      predictionManagerAxiosApi.post('/api/system/start'),
    ]);
    const orderbooksJob = orderbooksRes.status === 'fulfilled' ? orderbooksRes.value.data : null;
    const predictionsJob = predictionsRes.status === 'fulfilled' ? predictionsRes.value.data : null;
    return orderbooksJob || predictionsJob;
  }
  const res = await orderBookAxiosApi.post('/api/system/start', {
    subsystem: subsystem || 'orderbooks'
  });
  return res.data;
};

// Stop a subsystem
export const stopSystem = async (
  subsystem?: Subsystem
): Promise<SystemJob> => {
  if (subsystem === 'predictions') {
    const res = await predictionManagerAxiosApi.post('/api/system/stop');
    return res.data;
  }
  if (subsystem === 'all') {
    const [orderbooksRes, predictionsRes] = await Promise.allSettled([
      orderBookAxiosApi.post('/api/system/stop', { subsystem: 'orderbooks' }),
      predictionManagerAxiosApi.post('/api/system/stop'),
    ]);
    const orderbooksJob = orderbooksRes.status === 'fulfilled' ? orderbooksRes.value.data : null;
    const predictionsJob = predictionsRes.status === 'fulfilled' ? predictionsRes.value.data : null;
    return orderbooksJob || predictionsJob;
  }
  const res = await orderBookAxiosApi.post('/api/system/stop', {
    subsystem: subsystem || 'orderbooks'
  });
  return res.data;
};

// Master power toggle — stops opener + unwinder + predictions in one job.
// prediction-arbitrage-manager + notifications-websocket are intentionally
// left running, so TNA negative alerts keep flowing while everything else is
// off.
export const stopAll = async (): Promise<SystemJob> => {
  const res = await predictionManagerAxiosApi.post('/api/system/all/stop');
  return res.data;
};

// Inverse of stopAll — starts predictions + unwinder + opener in one job.
export const startAll = async (): Promise<SystemJob> => {
  const res = await predictionManagerAxiosApi.post('/api/system/all/start');
  return res.data;
};

/** One row of the cross-domain master panic result. */
export interface PanicAllDomainResult {
  domain: string;
  delegated: boolean;
  /** Redis keys root armed directly — byte-exact key names. */
  keysArmed: string[];
  /**
   * `null` = UNKNOWN: that domain's Mongo pause threw, so every rule in it is
   * still ACTIVE with its maker flags up. Never 0 for that case — a zero reads
   * as "nothing was active to pause", which is the opposite instruction.
   */
  rulesPaused: number | null;
  /**
   * Sniper only, and only when its delegated panic answered: whether the
   * orchestrator re-listed the sniper's venue accounts at zero. The
   * `*Remaining` counts are `null` when UNKNOWN.
   */
  verified?: boolean;
  kalshiRemaining?: number | null;
  polymarketRemaining?: number | null;
  errors: string[];
}

/** Root's `MasterPanicResult` — only `domains` is read by the UI. */
interface MasterPanicResponse {
  trigger?: string;
  domains?: PanicAllDomainResult[];
}

/**
 * Deadline for the master panic. `predictionManagerAxiosApi` is created with NO
 * timeout, so a wedged manager would leave this request pending forever — and
 * this is precisely the call an operator makes when things are already wedged.
 *
 * It must outlast ROOT's sniper delegate, which waits up to
 * {@link PANIC_ALL_SNIPER_DELEGATE_BUDGET_MS} for the sniper's VERIFIED cancel:
 * a shorter deadline turns a panic that is still verifying into a client error.
 */
export const PANIC_ALL_SNIPER_DELEGATE_BUDGET_MS = 90_000;
export const PANIC_ALL_TIMEOUT_MS = 120_000;

/**
 * Cross-domain master panic — arms every domain's halt/lockout keys and pauses
 * their rules in Mongo. Runs on the always-on manager so it works even when the
 * per-domain orchestrator pods are scaled to 0.
 */
export const panicAll = async (): Promise<PanicAllDomainResult[]> => {
  const res = await predictionManagerAxiosApi.post(
    '/api/system/panic/all',
    undefined,
    { timeout: PANIC_ALL_TIMEOUT_MS }
  );
  const data = res.data as MasterPanicResponse | null;
  return Array.isArray(data?.domains) ? data.domains : [];
};

// Aggregate prediction-side state for the master kill switch button.
export const getAllSystemState = async (): Promise<AllSystemState> => {
  const res = await predictionManagerAxiosApi.get<AllSystemState>(
    '/api/system/all/state',
    { timeout: SYSTEM_STATE_TIMEOUT_MS }
  );
  return res.data;
};

// 24h panic lockout state — armed by a STOP, gates START / rule activation
// until it expires (TTL) or an operator clears it.
export interface PanicLockoutState {
  armed: boolean;
  armedAt?: string;
  expiresAt?: string;
  reason?: string;
  ttlSeconds?: number;
}

// Position-unwinder controls (separate from predictions on/off and PANIC)
export interface UnwinderState {
  isRunning: boolean;
  replicas: number;
  lastUpdated: number;
  lastStopTriggeredPanic?: boolean;
  lastStopAt?: number;
  panicLockout?: PanicLockoutState;
}

export const getUnwinderState = async (): Promise<UnwinderState> => {
  const res = await unwinderOrchestratorAxiosApi.get<UnwinderState>(
    '/api/system/unwinder/state'
  );
  return res.data;
};

export const stopUnwinder = async (): Promise<SystemJob> => {
  const res = await unwinderOrchestratorAxiosApi.post(
    '/api/system/unwinder/stop'
  );
  return res.data;
};

export const startUnwinder = async (): Promise<SystemJob> => {
  const res = await unwinderOrchestratorAxiosApi.post(
    '/api/system/unwinder/start'
  );
  return res.data;
};

/**
 * Result of clearing one direction's panic keys. `haltCleared` is the placement
 * halt — the orchestrator-served clear only ever reported the lockout.
 */
export interface ClearPanicKeysResult {
  cleared: boolean;
  haltCleared: boolean;
  errors: string[];
}

/**
 * Both panic-key clears go to the MANAGER, not to the direction's orchestrator.
 *
 * The orchestrator is exactly what is missing when this button is needed: a
 * PANIC parks every opener group off, so the domain reads as unwanted and the
 * boot-only presence reconcile refuses to restart the control pod. START would
 * boot it — but START is refused while the lockout is armed, which is what the
 * operator is trying to clear. So ask the always-on pod, which is also the one
 * that ARMED both keys during the panic.
 */
export const clearUnwinderPanicLockout =
  async (): Promise<ClearPanicKeysResult> => {
    const res = await predictionManagerAxiosApi.delete<ClearPanicKeysResult>(
      '/api/system/unwinder/panic-lockout'
    );
    return res.data;
  };

// Position-opener controls — mirror of unwinder. The opener has its own
// /rules/panic endpoint that batch-cancels every active maker BUY across all
// rules and pauses every rule; prediction-manager invokes it before scaling
// the deployment to 0 if any active entry rules exist.
export interface OpenerState {
  /**
   * LIVENESS ONLY — true when at least one engine is up. Domain-level START
   * does not make this true: it opens the gate, and engines come up per group.
   * There is no "domain enabled" bit on the wire, so the UI must not claim one.
   */
  isRunning: boolean;
  replicas: number;
  lastUpdated: number;
  lastStopTriggeredPanic?: boolean;
  lastStopAt?: number;
  panicLockout?: PanicLockoutState;
  /** Per-group engine Deployments with readyReplicas > 0. */
  groupEnginesRunning?: number;
}

/**
 * Bounded like every other control-plane read: the orchestrator axios instance
 * carries no default timeout, and a pod that BLACKHOLES (node NotReady, a
 * security-group drop) leaves the TCP connect hanging instead of refusing it.
 * react-query 3 returns the in-flight promise rather than starting a second
 * fetch, so the 3s poll never retries either — the query would sit at
 * `isFetching` with the last successful snapshot for minutes, and the header
 * reads exactly that pair to decide the orchestrator is still answering.
 */
export const getOpenerState = async (): Promise<OpenerState> => {
  const res = await openerOrchestratorAxiosApi.get<OpenerState>(
    '/api/system/opener/state',
    { timeout: SYSTEM_STATE_TIMEOUT_MS }
  );
  return res.data;
};

export const stopOpener = async (): Promise<SystemJob> => {
  const res = await openerOrchestratorAxiosApi.post('/api/system/opener/stop');
  return res.data;
};

export const startOpener = async (): Promise<SystemJob> => {
  const res = await openerOrchestratorAxiosApi.post('/api/system/opener/start');
  return res.data;
};

/**
 * Venue-maintenance watch. The engines are halted account-wide whenever a
 * Kalshi/Polymarket maintenance window is open, and the watcher NEVER resumes
 * trading on its own — it halts, tells the operator when the window closed, and
 * waits here.
 *
 * Manager host, like the panic-key clears and for the same reason: this is the
 * always-on pod, it is the one that ARMED the halt, and asking a domain
 * orchestrator would fail in exactly the situations the button exists for.
 */
export interface VenueMaintenanceHeld {
  windowId: string;
  venue: string;
  title: string;
  startsAt: number;
  /**
   * Meaningful only when `endsAtKnown`. When the venue never published an end
   * (an unannounced outage, or a record with no usable length) this is a
   * placeholder the backend refuses to hold to, and the UI must not print it as
   * a schedule.
   */
  endsAt: number;
  endsAtKnown: boolean;
  /**
   * Epoch ms at which the auto-panic decision is (or was) taken — three minutes
   * before the window opens. THE deadline: while `autoPanicDeclined` is absent
   * and `enforced` is false, this is what the operator is counting down to and
   * the only number on this object they can still change the meaning of.
   */
  autoPanicAt?: number;
  /**
   * `true` when the auto-panic was OFF at `autoPanicAt` and this window will
   * NOT be cut. Three states, not two: ABSENT means the decision is still open
   * and the toggle still governs it. Collapsing absent into `true` would tell
   * an operator a window was declined while they still had six minutes to arm
   * it.
   */
  autoPanicDeclined?: boolean;
  /**
   * Whether a halt key is actually ARMED for this window. `false` covers BOTH
   * postures that armed nothing — the window is merely ANNOUNCED (the seven
   * minutes are running) or it was DECLINED — so BOTH engines are trading
   * straight through it. Absent from a backend older than this field — read it
   * as `true` there, which is what that backend always did.
   */
  enforced?: boolean;
  /**
   * Whether the window's operator PANIC actually ran.
   *
   * When it has, both halt keys carry `trigger: 'PANIC'` — the panic's own belt
   * wrote them — and they are STILL this window's: the halt-lift clears them,
   * and rendering them as somebody else's stop would send the operator chasing
   * a panic to clear that is the very window they are looking at. Absent from a
   * backend older than the field, where a window never fired a panic at all.
   */
  panicFired?: boolean;
  /**
   * Whether that panic actually DISARMED both directions.
   *
   * A different question from `panicFired`, and the one every "lifting this
   * restarts nothing" sentence depends on: the panic's belt arms the keys before
   * it pauses anything, and each layer beneath it swallows its own Mongo
   * failure, so a run can report on both directions with one direction's rules
   * all still ACTIVE. Absent from a backend older than the field.
   */
  panicDisarmed?: boolean;
  /**
   * `true` when this window is one of `VenueMaintenanceState.ambiguousWindows`:
   * the watcher could not tell whether it touches the CLOB and is holding it as
   * real. Absent from a backend older than the field.
   */
  ambiguous?: boolean;
  /**
   * `'writes_refused'` when this hold is the watcher's answer to a venue
   * refusing every write (`live:<venue>`), not an announced window. Absent on
   * a backend older than the field.
   */
  cause?: 'writes_refused';
  /** How a refusal-driven hold ended, once it is over. */
  recovery?: VenueWritesRecovery;
}

export type VenueWritesRecovery =
  | 'write_probe'
  | 'key_lapse'
  | 'cancels_accepted_placements_unverified';

/**
 * A maintenance the venue announced that the watcher cannot place. Polymarket's
 * status page changed shape (2026-10-08): the CLOB is no longer identifiable on
 * the board and the window names components we cannot map, so the watcher fails
 * CLOSED and treats it as a real window — auto-panic ON panics at T−3 like any
 * other, OFF declines it.
 */
export interface VenueMaintenanceAmbiguousWindow {
  venue: 'polymarket' | 'kalshi';
  id: string;
  title: string;
  /** Server epoch ms. */
  startsAt: number;
  endsAt: number;
  endsAtKnown: boolean;
}

/**
 * A venue answering EVERY write with a refusal — Polymarket's HTTP 425 "order
 * manager not ready" during the 2026-10-08 maintenance. The watcher treats a
 * SUSTAINED refusal as a degraded venue, the same as a maintenance window:
 * auto-panic ON halts and cancels everything, OFF declines it.
 */
export interface VenueWritesRefused {
  venue: 'polymarket' | 'kalshi';
  kind: 'restarting' | 'trading_disabled' | 'post_only';
  /** The HTTP status the venue answered with. */
  status: number;
  /** Server epoch ms. */
  since: number;
  lastSeenAt: number;
  retryAfterMs: number | null;
}

export interface VenueMaintenanceAutoPanic {
  /**
   * Whether a window is allowed to stop trading. OFF until somebody arms it —
   * an unattended account-wide panic is not a default.
   */
  enabled: boolean;
  /**
   * `null` when the backend has never READ the toggle successfully. NOT the
   * same as `false`: `false` is the operator's decision, `null` is what Redis
   * would not say, and rendering the second as the first tells them a switch
   * they armed is off. `enabled` above is still the fail-closed OFF default in
   * that case, because an unreadable switch may not arm a panic.
   */
  lastRead: boolean | null;
  changedAt: string | null;
  changedBy: string | null;
  readError: string | null;
}

export interface VenueMaintenanceState {
  enabled: boolean;
  /**
   * The operator's switch. Optional only for a backend older than the field —
   * the FE ships through CloudFront independently of EKS, so undefined is
   * reachable by deploy order alone.
   */
  autoPanic?: VenueMaintenanceAutoPanic;
  /**
   * How early a window is announced, and how early the auto-panic fires.
   * Served so the two numbers have ONE source of truth; the bar renders the
   * deadline the backend computed (`held.autoPanicAt`) rather than deriving it
   * from these. The one exception is an ambiguous window with no hold yet:
   * there is no `autoPanicAt` to read, so its panic time is
   * `startsAt − panicLeadMs`.
   */
  announceLeadMs?: number;
  panicLeadMs?: number;
  /**
   * The BACKEND's clock, epoch ms, as of answering.
   *
   * `held.autoPanicAt` is a server epoch, so every question about it has to be
   * asked in server time. Comparing it against `Date.now()` compares two
   * clocks, and the operator's laptop is the one that drifts — a machine two
   * minutes fast reads a deadline that has not arrived as one the watcher
   * MISSED. Optional: absent on a backend older than the field, where the skew
   * falls back to zero and the page behaves exactly as it did.
   */
  serverNow?: number;
  held: VenueMaintenanceHeld | null;
  /**
   * Announced windows the watcher cannot verify, present BEFORE any hold. Absent
   * on a backend older than the field; absent or empty means none.
   */
  ambiguousWindows?: VenueMaintenanceAmbiguousWindow[];
  /**
   * Venues currently refusing every write. Absent on a backend older than the
   * field; absent or empty means none.
   */
  writesRefused?: VenueWritesRefused[];
  /** The window is over and it is safe to resume — nothing has resumed yet. */
  awaitingOperator: boolean;
  heldSince: string | null;
  /**
   * What is ACTUALLY armed on each direction's halt key right now, by trigger
   * (`VENUE_MAINTENANCE` | `PANIC` | `STOP`), or null when that direction is
   * free to place. Read straight through to Redis, so it survives a pod restart
   * and a page navigation and self-corrects when someone clears the other halt
   * — none of which a verdict cached in the browser can do.
   */
  halts: { opener: string | null; unwinder: string | null };
  /** Directions whose halt could NOT be armed — they may still be placing. */
  haltArmFailures: string[];
  overriddenVenue: string | null;
  /**
   * The window the operator lifted the halt for. The watcher does not
   * re-decide it while the venue keeps listing it. Absent on an older backend.
   */
  overriddenWindowId?: string | null;
}

export interface VenueMaintenanceResumeResult {
  resumed: boolean;
  /** False when the key was NOT cleared — a trigger this control may not lift, or a clear that failed. */
  haltCleared: boolean;
  windowId: string | null;
}

export const getVenueMaintenanceState =
  async (): Promise<VenueMaintenanceState> => {
    const res = await predictionManagerAxiosApi.get<VenueMaintenanceState>(
      '/api/system/venue-maintenance'
    );
    return res.data;
  };

export interface VenueMaintenanceAutoPanicResult {
  enabled: boolean;
  changedAt: string;
  /**
   * The window whose decision this call did NOT change, or `null`. The toggle
   * governs the NEXT decision: one already taken (panicked, or declined) is
   * latched, and a 200 that said nothing would read as "done" to an operator
   * pressing it to change the window on screen.
   */
  windowUnaffected: string | null;
}

export const setVenueMaintenanceAutoPanic = async (
  enabled: boolean
): Promise<VenueMaintenanceAutoPanicResult> => {
  const res =
    await predictionManagerAxiosApi.post<VenueMaintenanceAutoPanicResult>(
      '/api/system/venue-maintenance/auto-panic',
      { enabled }
    );
  return res.data;
};

export const resumeAfterVenueMaintenance =
  async (): Promise<VenueMaintenanceResumeResult> => {
    const res =
      await predictionManagerAxiosApi.post<VenueMaintenanceResumeResult>(
        '/api/system/venue-maintenance/resume'
      );
    return res.data;
  };

/** Mirror of `clearUnwinderPanicLockout` — same reason, same host. */
export const clearOpenerPanicLockout =
  async (): Promise<ClearPanicKeysResult> => {
    const res = await predictionManagerAxiosApi.delete<ClearPanicKeysResult>(
      '/api/system/opener/panic-lockout'
    );
    return res.data;
  };

// Poll job progress (checks both services, returns whichever is running)
export const getSystemJob = async (
  scope: SystemStateScope = 'all'
): Promise<SystemJob | null> => {
  if (scope === 'predictions') {
    try {
      const res = await predictionManagerAxiosApi.get('/api/system/job', {
        timeout: SYSTEM_STATE_TIMEOUT_MS,
      });
      return res.data;
    } catch {
      return null;
    }
  }

  const [orderbooksRes, predictionsRes] = await Promise.allSettled([
    orderBookAxiosApi.get('/api/system/job'),
    predictionManagerAxiosApi.get('/api/system/job'),
  ]);
  const orderbooksJob = orderbooksRes.status === 'fulfilled' ? orderbooksRes.value.data : null;
  const predictionsJob = predictionsRes.status === 'fulfilled' ? predictionsRes.value.data : null;
  if (orderbooksJob?.status === 'running') return orderbooksJob;
  if (predictionsJob?.status === 'running') return predictionsJob;
  return orderbooksJob || predictionsJob;
};

// Restart specific connectors (or all if exchanges is empty)
export const restartConnectors = async (
  exchanges?: string[]
): Promise<RestartConnectorsResult> => {
  const res = await orderBookAxiosApi.post('/api/system/restart-connectors', {
    exchanges
  });
  return res.data;
};

// Get connector exchanges available in connectors cluster
export const getConnectorExchanges =
  async (): Promise<ConnectorExchangesResult> => {
    const res = await orderBookAxiosApi.get('/api/system/connectors');
    return res.data;
  };

// Resubscribe to orderbooks (reset snapshots)
export const putResubscribeOrderbook = async (ids?: Exchange['id'][]) => {
  const querystring = ids?.length
    ? qs.stringify({ ids }, { addQueryPrefix: true })
    : '';

  return await orderBookAxiosApi.put(
    endpoints.EXCHANGES + `/resubscribe${querystring}`
  );
};
